import React, { useEffect, useState } from "react";
import "./Customer.css";
import { Avatar, Col, Grid, Popover, Tag, Typography } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import CustomerHeader from "./CustomerHeader";
import CustomTableWithPagination from "../../components/CustomTableWithPagination/CustomTableWithPagination";
import { ReactComponent as DefaultIcon } from "../../assets/icons/default.svg";
import {
  useLazyGetCustomersQuery,
  useLazyGetDownloadCustomersQuery,
} from "../../apis/customer";
import { baseUrl } from "../../commonUtils/commonUtils";
import { dateFormat } from "../../commonFuntions/CommonUtilFunctions";
import TableSkeleton from "../../components/skeleton/TableSkeleton";
import NoDataFound from "../../components/EmptyTable/NoDataFound";

const { Text } = Typography;

const Customer = () => {
  const screens = Grid.useBreakpoint();
  const isMobileViewport = !screens.md;
  const [searchedValue, setSearchedValue] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  const [
    getAllCustomersApi,
    {
      data: tableData,
      isFetching,
    },
  ] = useLazyGetCustomersQuery();

  const [downloadCustomersApi] = useLazyGetDownloadCustomersQuery();

  useEffect(() => {
    getAllCustomersApi();
  }, [getAllCustomersApi]);

  const handleSearchedValue = (params) => {
    setSearchedValue(params);
    setCurrentPage(1);
  };

  const handlePageChange = async (currPage) => {
    const params = {
      page: currPage,
      ...(searchedValue?.search ? { search: searchedValue.search } : {}),
    };
    setCurrentPage(currPage);
    await getAllCustomersApi(params);
  };

  const getStatusTagColor = (status) => {
    const normalized = String(status || "").toUpperCase();
    if (normalized === "ACTIVE") return "success";
    if (normalized === "INACTIVE") return "default";
    if (normalized === "SUSPENDED") return "warning";
    if (normalized === "BLOCKED") return "error";
    return "default";
  };

  const getMobCreditUIStatus = (primaryStatus, accountStatus) => {
    const primary = String(primaryStatus || "").toUpperCase();
    const secondary = String(accountStatus || "").toUpperCase();
    if (primary === "ACTIVE" && secondary === "ACTIVE") return { label: "ACTIVE", color: "success" };
    if (primary === "ACTIVE" && secondary === "INACTIVE") return { label: "INACTIVE", color: "orange" };
    if (primary === "REJECTED") return { label: "REJECTED", color: "error" };
    if (primary === "PRE_APPROVED" || primary === "INCOMPLETE" || primary === "UNDER_REVIEW") {
      return { label: "IN PROGRESS", color: "gold" };
    }
    return { label: primaryStatus || "-", color: "default" };
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      fixed: isMobileViewport ? undefined : "left",
      width: "250px",
      render: (params, record) => (
        <Col
          style={{
            display: "flex",
            gap: "20px",
            alignItems: "center",
            paddingLeft: record?.parentKey ? "60px" : "0px",
          }}
        >
          <Col>
            <Avatar
              style={{
                width: "48px",
                height: "48px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#f1f1f2",
              }}
              shape="square"
              src={`${baseUrl}${record?.profile_picture}`}
              alt="Avatar"
              icon={<DefaultIcon width={20} height={20} />}
            />
          </Col>
          {params || record?.name || "-"}
        </Col>
      ),
    },
    {
      title: "Business mobile",
      dataIndex: "phone_number",
      key: "phone_number",
      width: "200px",
      render: (params) => <Text>{params || "-"}</Text>,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: "150px",
      render: (params) => <Text>{params || "-"}</Text>,
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      width: "300px",
      render: (value, record) => {
        if (typeof value === "string") return value;
        if (Array.isArray(value)) {
          const firstAddress = value[0];
          if (firstAddress && typeof firstAddress === "object") {
            return [
              firstAddress.address_line_1,
              firstAddress.address_line_2,
              firstAddress.city,
              firstAddress.state,
              firstAddress.pincode,
            ]
              .filter(Boolean)
              .join(", ");
          }
          return "-";
        }
        if (value && typeof value === "object") {
          return [
            value.address_line_1,
            value.address_line_2,
            value.city,
            value.state,
            value.pincode,
          ]
            .filter(Boolean)
            .join(", ");
        }
        return [
          record?.address_line_1,
          record?.address_line_2,
          record?.city,
          record?.state,
          record?.pincode,
        ]
          .filter(Boolean)
          .join(", ");
      },
    },
    {
      title: "Business Segment",
      dataIndex: "business_segment",
      key: "business_segment",
      width: "220px",
      render: (segment) => {
        const category = segment?.category || "-";
        const popoverContent = (
          <div className="business-segment-popover-content">
            <div className="business-segment-popover-row">
              <span className="business-segment-popover-label">Category Name:</span>{" "}
              <span>{segment?.category_name || "-"}</span>
            </div>
            <div className="business-segment-popover-row">
              <span className="business-segment-popover-label">Line of Credit Interest:</span>{" "}
              <span>{segment?.line_of_credit_interest ?? "-"}</span>
            </div>
            <div className="business-segment-popover-row">
              <span className="business-segment-popover-label">Interest After:</span>{" "}
              <span>{segment?.interest_after ?? "-"}</span>
            </div>
            <div className="business-segment-popover-row">
              <span className="business-segment-popover-label">MOB Partner Fees:</span>{" "}
              <span>{segment?.mob_partner_fees ?? "-"}</span>
            </div>
            <div className="business-segment-popover-row">
              <span className="business-segment-popover-label">Terms:</span>{" "}
              <span>{segment?.terms_and_conditions || "-"}</span>
            </div>
          </div>
        );

        return (
          <span className="business-segment-cell">
            <span>{category}</span>
            <Popover
              content={popoverContent}
              trigger="click"
              placement="bottomLeft"
              overlayClassName="business-segment-popover"
            >
              <InfoCircleOutlined style={{ color: "#8c8c8c", cursor: "pointer" }} />
            </Popover>
          </span>
        );
      },
    },
    {
      title: "MOB Credit(Rupifi)",
      dataIndex: "mobCredit",
      key: "mobCredit",
      width: "220px",
      render: (mobCredit) => {
        const { label: mobCreditLabel, color: mobCreditColor } = getMobCreditUIStatus(
          mobCredit?.primary_status,
          mobCredit?.account_status
        );
        const popoverContent = (
          <div className="business-segment-popover-content">
            <div className="business-segment-popover-row">
              <span className="business-segment-popover-label">Primary Status:</span>{" "}
              <Tag color={getStatusTagColor(mobCredit?.primary_status)}>
                {mobCredit?.primary_status || "-"}
              </Tag>
            </div>
            <div className="business-segment-popover-row">
              <span className="business-segment-popover-label">Account Status:</span>{" "}
              <Tag color={getStatusTagColor(mobCredit?.account_status)}>
                {mobCredit?.account_status || "-"}
              </Tag>
            </div>
            <div className="business-segment-popover-row">
              <span className="business-segment-popover-label">Current Limit:</span>{" "}
              <span>
                {mobCredit?.current_limit !== undefined &&
                mobCredit?.current_limit !== null
                  ? `₹ ${mobCredit.current_limit}`
                  : "-"}
              </span>
            </div>
            <div className="business-segment-popover-row">
              <span className="business-segment-popover-label">Balance:</span>{" "}
              <span>
                {mobCredit?.balance !== undefined && mobCredit?.balance !== null
                  ? `₹ ${mobCredit.balance}`
                  : "-"}
              </span>
            </div>
          </div>
        );

        return (
          <span className="business-segment-cell">
            <Tag color={mobCreditColor}>{mobCreditLabel}</Tag>
            <Popover
              content={popoverContent}
              trigger="click"
              placement="bottomLeft"
              overlayClassName="business-segment-popover"
            >
              <InfoCircleOutlined style={{ color: "#8c8c8c", cursor: "pointer" }} />
            </Popover>
          </span>
        );
      },
    },
    {
      title: "Registered date",
      dataIndex: "created_at",
      key: "created_at",
      width: "200px",
      render: (params) => (
        <Col>
          <Text>{params ? dateFormat(params) : "-"}</Text>
        </Col>
      ),
    },
    {
      title: "GSTIN",
      dataIndex: "gst_number",
      key: "gst_number",
      width: "200px",
      render: (params) => <Text>{params || "-"}</Text>,
    },
  ];

  return (
    <Col className="customer-container">
      <Text className="customer_title">Customers</Text>
      <CustomerHeader
        getAllCustomersApi={getAllCustomersApi}
        isFetching={isFetching}
        downloadCustomersApi={downloadCustomersApi}
        handleSearchedValue={handleSearchedValue}
        showAddCustomer={false}
        showExport={false}
      />
      <CustomTableWithPagination
        className="order-list-table"
        tableDataSource={tableData?.data?.results}
        tableColumns={columns}
        locale={{
          emptyText: isFetching ? (
            <TableSkeleton length={10} />
          ) : (
            <NoDataFound text={"No customers found!"} />
          ),
        }}
        isLoading={tableData?.data?.results?.length && isFetching}
        scroll={{ x: isMobileViewport ? 1100 : "100%" }}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
        itemsPerPage={30}
        totalEntries={!isFetching && tableData?.data?.pagination?.total_entries}
      />
    </Col>
  );
};

export default Customer;
